import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MultiSelect from '../components/MultiSelect';

const STATUS_META = {
  '1. Sab Zero':   { color:'var(--red)',    bg:'var(--red-bg)',    border:'var(--red-bd)',    label:'Sab Zero',  desc:'DP + MOP + NLC all zero' },
  '2. DP Zero':    { color:'var(--amber)',  bg:'var(--amber-bg)',  border:'var(--amber-bd)',  label:'DP Zero',   desc:'Dealer Price not set' },
  '3. MOP Zero':   { color:'var(--amber)',  bg:'var(--amber-bg)',  border:'var(--amber-bd)',  label:'MOP Zero',  desc:'Market Op. Price not set' },
  '4. NLC Zero':   { color:'var(--purple)', bg:'var(--purple-bg)', border:'var(--purple-bd)', label:'NLC Zero',  desc:'Net Landing Cost not set' },
  '5. MOP <= NLC': { color:'var(--blue)',   bg:'var(--blue-bg)',   border:'var(--blue-bd)',   label:'MOP ≤ NLC', desc:'MOP is below cost' },
  '6. DP <= MOP':  { color:'var(--red)',    bg:'var(--red-bg)',    border:'var(--red-bd)',    label:'DP ≤ MOP',  desc:'DP not above MOP' },
};

function fmtPrice(n) {
  if (n === null || n === undefined) return <span className="muted">—</span>;
  if (n === 0) return <span style={{ color: 'var(--red)', fontWeight: 600 }}>₹0</span>;
  return '₹' + Number(n).toLocaleString('en-IN');
}

const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

const PFX = 'p_';
function getP(key) { return new URLSearchParams(window.location.search).get(PFX + key) || ''; }
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = Array.isArray(val) ? val.join('|') : (val || '');
  if (v && v !== 'ALL') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearAllPParams() {
  const sp = new URLSearchParams(window.location.search);
  ['search','status','pgrp','make','stock','b22'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

export default function PricingIssues({ db }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [sortCol, setSortCol] = useState('Status');
  const [sortDir, setSortDir] = useState('asc');

  const [search,       setSearchRaw]       = useState(() => getP('search'));
  const [filterStatus, setFilterStatusRaw] = useState(() => getP('status') || 'ALL');
  const [filterPGrp,   setFilterPGrpRaw]   = useState(() => { const v = getP('pgrp'); return v ? v.split('|') : []; });
  const [filterMake,   setFilterMakeRaw]   = useState(() => { const v = getP('make'); return v ? v.split('|') : []; });
  const [filterStock,  setFilterStockRaw]  = useState(() => getP('stock') || 'ALL');
  const [filterB22,    setFilterB22Raw]    = useState(() => getP('b22')   || 'ALL');

  const setSearch       = v => { setSearchRaw(v);       setP('search', v); };
  const setFilterStatus = v => { setFilterStatusRaw(v); setP('status', v); };
  const setFilterPGrp   = v => { setFilterPGrpRaw(v);   setP('pgrp',  v); };
  const setFilterMake   = v => { setFilterMakeRaw(v);   setP('make',  v); };
  const setFilterStock  = v => { setFilterStockRaw(v);  setP('stock', v); };
  const setFilterB22    = v => { setFilterB22Raw(v);    setP('b22',   v); };

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/pricing-issues?db=${db}`, { headers: { Authorization: `Bearer ${token()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [db]);

  useEffect(() => { if (db) fetchData(); }, [fetchData, db]);

  const allPGrps = useMemo(() => [...new Set(data.map(d => d.PGrp).filter(Boolean).sort())], [data]);
  const availableMakes = useMemo(() => {
    const src = filterPGrp.length > 0 ? data.filter(d => filterPGrp.includes(d.PGrp)) : data;
    return [...new Set(src.map(d => d.Make).filter(Boolean).sort())];
  }, [data, filterPGrp]);

  useEffect(() => {
    if (filterMake.length > 0) {
      const valid = filterMake.filter(m => availableMakes.includes(m));
      if (valid.length !== filterMake.length) { setFilterMakeRaw(valid); setP('make', valid); }
    }
  }, [availableMakes]); // eslint-disable-line

  const baseFiltered = useMemo(() => {
    let d = data;
    if (filterPGrp.length > 0)     d = d.filter(r => filterPGrp.includes(r.PGrp));
    if (filterMake.length > 0)     d = d.filter(r => filterMake.includes(r.Make));
    if (filterStock === 'nonzero') d = d.filter(r => (r.CurrentStock || 0) > 0);
    if (filterStock === 'zero')    d = d.filter(r => (r.CurrentStock || 0) <= 0);
    if (filterB22 === 'yes')       d = d.filter(r => !!r.B22);
    if (filterB22 === 'no')        d = d.filter(r => !r.B22);
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        (r.ItemName||'').toLowerCase().includes(q) ||
        (r.Make||'').toLowerCase().includes(q) ||
        (r.Grp||'').toLowerCase().includes(q) ||
        String(r.ItemCode).includes(q)
      );
    }
    return d;
  }, [data, filterPGrp, filterMake, filterStock, filterB22, search]);

  const counts = useMemo(() => {
    const c = {};
    baseFiltered.forEach(d => { c[d.Status] = (c[d.Status] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  const stats = useMemo(() => {
    const withStock = baseFiltered.filter(d => (d.CurrentStock || 0) > 0);
    const critical  = baseFiltered.filter(d => d.Status === '1. Sab Zero' || d.Status === '2. DP Zero');
    return { total: baseFiltered.length, withStock: withStock.length, critical: critical.length };
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    let d = baseFiltered;
    if (filterStatus !== 'ALL') d = d.filter(r => r.Status === filterStatus);
    return [...d].sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [baseFiltered, filterStatus, sortCol, sortDir]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }
  function Th({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={active ? 'sorted' : ''} onClick={() => handleSort(col)}>
        {children}<span className="sort-arrow">{active ? (sortDir==='asc'?'↑':'↓') : '↕'}</span>
      </th>
    );
  }

  function clearAll() {
    setFilterStatusRaw('ALL'); setFilterPGrpRaw([]); setFilterMakeRaw([]);
    setFilterStockRaw('ALL'); setFilterB22Raw('ALL'); setSearchRaw('');
    clearAllPParams();
  }
  const anyFilter = filterStatus !== 'ALL' || filterPGrp.length || filterMake.length
    || filterStock !== 'ALL' || filterB22 !== 'ALL' || search;

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="page-title">Pricing Issues</div>
        <div className="page-sub">Items with missing or inconsistent prices · SAHI excluded · {db}</div>
      </div>

      <div className="summary-cards">
        <div className="sum-card">
          <div className="sum-card-label">Total Issues</div>
          <div className="sum-card-value red">{stats.total.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">items with price problems</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">In Stock</div>
          <div className="sum-card-value orange">{stats.withStock.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">have current stock</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Critical</div>
          <div className="sum-card-value red">{stats.critical.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">Sab Zero or DP Zero</div>
        </div>
      </div>

      {error && (
        <div className="error-bar">
          <span className="error-icon">⚠</span>
          <div><div>Failed to load data</div><div className="error-detail">{error}</div></div>
        </div>
      )}

      <div className="stats-row-scroll">
        <button className="stat-chip"
          style={{ color: 'var(--text2)', borderColor: 'var(--border)', background: filterStatus === 'ALL' ? 'var(--surface2)' : 'transparent' }}
          onClick={() => setFilterStatus('ALL')}>
          <span className="stat-dot" style={{ background: 'var(--text3)' }} />
          All ({baseFiltered.length})
        </button>
        {Object.entries(STATUS_META).map(([key, m]) => (
          <button key={key} className="stat-chip"
            style={{ color: m.color, background: filterStatus === key ? m.bg : 'transparent', borderColor: m.border }}
            onClick={() => setFilterStatus(s => s === key ? 'ALL' : key)}>
            <span className="stat-dot" style={{ background: m.color }} />
            {m.label} ({counts[key] || 0})
          </button>
        ))}
      </div>

      <div className="toolbar">
        <div className="toolbar-row-a">
          <div className="search-box">
            <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input placeholder="Search item, brand, group, code…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className={`btn-refresh${loading ? ' spinning' : ''}`} onClick={fetchData} disabled={loading}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M1 4v6h6M23 20v-6h-6" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
        <div className="toolbar-row-b">
          <MultiSelect label="Parent Grp" options={allPGrps} selected={filterPGrp}
            onChange={v => { setFilterPGrp(v); setFilterMakeRaw([]); setP('make', []); }} />
          <MultiSelect label="Brand" options={availableMakes} selected={filterMake} onChange={setFilterMake} />
          <select className="filter-select" value={filterStock} onChange={e => setFilterStock(e.target.value)}>
            <option value="ALL">Stock: All</option>
            <option value="nonzero">In Stock Only</option>
            <option value="zero">Zero Stock</option>
          </select>
          <select className="filter-select" value={filterB22} onChange={e => setFilterB22(e.target.value)}>
            <option value="ALL">Serial: All</option>
            <option value="yes">B22 = Yes</option>
            <option value="no">B22 = No</option>
          </select>
          <button className="btn-reset" onClick={clearAll} disabled={!anyFilter}>↺ Reset</button>
          <span className="count-badge">{filtered.length} rows</span>
        </div>
      </div>

      {loading && !data.length ? (
        <div className="loading-state"><div className="spinner" /><span>Loading…</span></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <Th col="Status">Status</Th>
                <Th col="ItemCode">Code</Th>
                <Th col="ItemName">Item Name</Th>
                <Th col="Make">Brand</Th>
                <Th col="PGrp">Parent Grp</Th>
                <Th col="Grp">Group</Th>
                <Th col="DP">DP</Th>
                <Th col="MOP">MOP</Th>
                <Th col="NLC">NLC</Th>
                <Th col="B22">B22</Th>
                <Th col="CurrentStock">Stock</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11}>
                  <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3>No records found</h3>
                    <p>Try adjusting your filters</p>
                  </div>
                </td></tr>
              ) : filtered.map((r, i) => {
                const m     = STATUS_META[r.Status] || {};
                const stock = r.CurrentStock || 0;
                return (
                  <tr key={`${r.ItemCode}-${i}`}>
                    <td>
                      <span className="badge" style={{ color: m.color, background: m.bg, borderColor: m.border }}>
                        {m.label || r.Status}
                      </span>
                    </td>
                    <td className="mono dim">{r.ItemCode}</td>
                    <td className="wrap">{r.ItemName}</td>
                    <td className="dim">{r.Make || <span className="muted">—</span>}</td>
                    <td className="dim">{r.PGrp || <span className="muted">—</span>}</td>
                    <td className="dim">{r.Grp  || <span className="muted">—</span>}</td>
                    <td className="num">{fmtPrice(r.DP)}</td>
                    <td className="num">{fmtPrice(r.MOP)}</td>
                    <td className="num">{fmtPrice(r.NLC)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {r.B22
                        ? <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 11 }}>✓ Yes</span>
                        : <span className="muted" style={{ fontSize: 11 }}>No</span>}
                    </td>
                    <td className="num">
                      <span style={{ color: stock > 0 ? 'var(--amber)' : 'var(--text3)', fontWeight: stock > 0 ? 600 : 400 }}>
                        {stock > 0 ? stock.toLocaleString('en-IN') : <span className="muted">0</span>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
